<script setup lang="ts">
import { canRetryImageFallback } from '~/utils/albumMedia.mjs'

const props = withDefaults(defineProps<{
  src: string
  fallbackSrc?: string
  sources?: Array<{ url: string; type: string }>
  alt: string
  loading?: 'lazy' | 'eager'
  progressive?: boolean
}>(), { fallbackSrc: '', sources: () => [], loading: 'lazy', progressive: true })

const emit = defineEmits<{ load: []; error: [] }>()
const picture = ref<HTMLPictureElement | null>(null)
const usingFallback = ref(false)
const failed = ref(false)
let failedImage: HTMLImageElement | null = null

// handleError 切换一次兼容图，移除候选源避免浏览器再次选择失败地址
const handleError = () => {
  const image = picture.value?.querySelector('img')
  if (!image || image === failedImage) return
  failedImage = image
  if (!usingFallback.value && canRetryImageFallback(image.currentSrc || image.src, props.fallbackSrc, document.baseURI)) {
    usingFallback.value = true
    return
  }
  failed.value = true
  emit('error')
}

watch([() => props.src, () => props.fallbackSrc, () => JSON.stringify(props.sources)], () => {
  usingFallback.value = false
  failed.value = false
  failedImage = null
})
</script>

<template>
  <picture ref="picture" class="media-image" :class="{ 'is-error': failed }">
    <source
      v-for="source in usingFallback ? [] : sources"
      :key="`${source.type}-${source.url}`"
      :srcset="source.url"
      :type="source.type"
      media="(dynamic-range: high)"
    >
    <BlogImage
      :key="`${src}:${usingFallback}`"
      :src="usingFallback ? fallbackSrc : src"
      :alt="alt"
      :loading="loading"
      :progressive="progressive"
      @load="emit('load')"
      @error="handleError"
    />
    <BlogAlbumImageWarning v-if="failed" />
  </picture>
</template>

<style scoped>
.media-image {
  position: relative;
  display: block;
  width: 100%;
}

.media-image :deep(img) {
  display: block;
  width: 100%;
}

.media-image.is-error :deep(img) {
  visibility: hidden;
}

.media-image.is-error {
  min-height: var(--album-placeholder-height, 100%);
}
</style>
